import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SrIcon } from "@/components/ui/SrIcon";
import { formatNOK } from "@/lib/format";
import {
  useStoragePricingTiers,
  useStoragePricingTierMutations,
} from "@/hooks/use-storage-pricing-tiers";

/**
 * VIB-1308: admin registrerer trinnpriser for lagringsfakturering. Satsene
 * (B1–B3) er blokkert på Sølvrev-avklaring — panelet er tomt til de foreligger,
 * og fakturamotoren fakturerer 0 storage-avtaler så lenge tabellen er tom.
 */
export function StoragePricingTiersPanel() {
  const { data: tiers = [], isLoading } = useStoragePricingTiers();
  const { createTier, softDeleteTier } = useStoragePricingTierMutations();

  const [minGb, setMinGb] = useState("");
  const [maxGb, setMaxGb] = useState("");
  const [price, setPrice] = useState("");
  const [priceModel, setPriceModel] = useState<"flat" | "per_gb">("flat");

  const canAdd = minGb !== "" && price !== "" && Number(price) > 0;

  const handleAdd = () => {
    createTier.mutate(
      {
        min_gb: Number(minGb),
        max_gb: maxGb === "" ? null : Number(maxGb),
        price: Number(price),
        price_model: priceModel,
        valid_from: new Date().toISOString().split("T")[0],
      },
      {
        onSuccess: () => {
          setMinGb("");
          setMaxGb("");
          setPrice("");
          setPriceModel("flat");
        },
      },
    );
  };

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center gap-2 mb-2">
          <SrIcon name="database" className="h-4 w-4 text-muted-foreground" />
          <h2 className="font-display text-base">Lagring — trinnpriser</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Stegvise priser for lagring av råmateriale etter kundens totale GB. Satsene avklares med Sølvrev
          (trinngrenser, pris og prismodell) — til de er lagt inn her, faktureres ingen lagring.
        </p>

        {isLoading ? (
          <div className="flex justify-center py-6">
            <SrIcon name="loader2" className="w-4 h-4 animate-spin text-muted-foreground" />
          </div>
        ) : tiers.length === 0 ? (
          <p className="text-sm text-muted-foreground mb-4 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-amber-800">
            Ingen pristrinn registrert ennå. Legg til trinn når satsene er avklart.
          </p>
        ) : (
          <div className="overflow-x-auto mb-4">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-2 font-medium text-muted-foreground text-xs uppercase tracking-wider">Fra GB</th>
                  <th className="text-left py-2 px-2 font-medium text-muted-foreground text-xs uppercase tracking-wider">Til GB</th>
                  <th className="text-left py-2 px-2 font-medium text-muted-foreground text-xs uppercase tracking-wider">Modell</th>
                  <th className="text-right py-2 px-2 font-medium text-muted-foreground text-xs uppercase tracking-wider">Pris</th>
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody>
                {tiers.map((t) => (
                  <tr key={t.id} className="border-b border-border/50 last:border-0">
                    <td className="py-2 px-2 tabular-nums">{t.min_gb}</td>
                    <td className="py-2 px-2 tabular-nums">{t.max_gb ?? "∞"}</td>
                    <td className="py-2 px-2">{t.price_model === "per_gb" ? "Per GB" : "Fast"}</td>
                    <td className="py-2 px-2 text-right tabular-nums">
                      {formatNOK(t.price)}{t.price_model === "per_gb" ? " /GB" : ""}
                    </td>
                    <td className="py-2 px-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-destructive"
                        onClick={() => softDeleteTier.mutate(t.id)}
                        aria-label="Fjern pristrinn"
                      >
                        <SrIcon name="trash" className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 items-end">
          <div className="space-y-1">
            <Label htmlFor="tier-min-gb" className="text-xs">Fra GB</Label>
            <Input id="tier-min-gb" type="number" min={0} value={minGb} onChange={(e) => setMinGb(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="tier-max-gb" className="text-xs">Til GB (tom = ∞)</Label>
            <Input id="tier-max-gb" type="number" min={0} value={maxGb} onChange={(e) => setMaxGb(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Modell</Label>
            <Select value={priceModel} onValueChange={(v) => setPriceModel(v as "flat" | "per_gb")}>
              <SelectTrigger aria-label="Prismodell"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="flat">Fast</SelectItem>
                <SelectItem value="per_gb">Per GB</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="tier-price" className="text-xs">Pris (kr)</Label>
            <Input id="tier-price" type="number" min={0} value={price} onChange={(e) => setPrice(e.target.value)} />
          </div>
          <Button onClick={handleAdd} disabled={!canAdd || createTier.isPending}>
            <SrIcon name="plus" className="w-4 h-4 mr-1" /> Legg til
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
